import { CommonModule } from '@angular/common';
import { JobSaveGateService } from '../job-save-gate.service';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { Store, createSelector } from '@ngrx/store';

import { MarkdownModule } from 'ngx-markdown';
import { MessageService } from 'primeng/api';
import { DividerModule } from 'primeng/divider';
import { InputTextareaModule } from 'primeng/inputtextarea';

import { firstValueFrom } from 'rxjs';

import { UpdateJobGQL } from 'src/generated/graphql.generated';

import { dayjs } from '../../core/dayjs/dayjs';
import {  createConfigSelector } from '../../state/branding.store';
import { JobToolActions } from '../job-tool.actions';
import { selectJob, selectJobInCurrentZone, selectOpsNotes } from '../job-tool.reducer';

import { OverviewCustomerComponent } from "./overview-customer/overview-customer.component";
import { OverviewHeaderComponent } from "./overview-header/overview-header.component";
import { OverviewLocationsComponent } from './overview-locations/overview-locations.component';
import { OverviewOpsTasksComponent } from './overview-ops-tasks/overview-ops-tasks.component';
import { OverviewSummaryComponent } from './overview-summary/overview-summary.component';
import { OverviewTimelineComponent } from "./overview-timeline/overview-timeline.component";

@Component({
  selector: 'app-job-overview',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MarkdownModule,
    DividerModule,
    InputTextareaModule,
    OverviewHeaderComponent,
    OverviewCustomerComponent,
    OverviewLocationsComponent,
    OverviewTimelineComponent,
    OverviewSummaryComponent,
    OverviewOpsTasksComponent
  ],
  templateUrl: './job-overview.component.html',
  styleUrls: [
    './job-overview.component.scss',
    '../job-overview/overview-header/overview-chip/shared.overview-chip.component.scss',
  ],
})
export class JobOverviewComponent {

  public viewModel$ = this.store.select(createSelector({
    job: selectJob,
    jobNotInCurrentZone: createSelector(selectJobInCurrentZone, (inZone) => !inZone),
    heartbeatSummary: createSelector(selectJob, (job) => job?.heartbeatSummary ?? null),
    heartbeatEnabled: createConfigSelector('JOBSHEARTBEAT_ENABLED' as any, false),
    heartbeatLastRan: createSelector(selectJob, (job) => job?.lastHeartbeat ?? null),
    opsNotes: selectOpsNotes,
  }));

  constructor(
    public store: Store,
    private updateJobGQL: UpdateJobGQL,
    private messageService: MessageService,
    private saveGate: JobSaveGateService,
  ) { }

  dayjs = dayjs;

  // ---- Ops Summary (Ray: the Overview's Ops Summary should be editable —
  // #6983). One summary per JOB (Kozak's call) — the exact same
  // job-level `metadata.calculatorOutput__opsNotes` the Dispatch page's
  // mini-card "Ops summary" textareas already write, so the two stay in
  // sync. Drafts keyed by job; saved on blur (dirty check).
  public opsNotesDrafts = new Map<string, string>();
  public opsNotesSaving = false;

  public getOpsNotesDraft(jobId: string, saved: string): string {
    return this.opsNotesDrafts.get(jobId) ?? (saved || '');
  }

  public onOpsNotesInput(jobId: string, value: string): void {
    this.opsNotesDrafts.set(jobId, value);
  }

  public async onOpsNotesBlur(job: { id: string } | undefined | null, saved: string): Promise<void> {
    if (!job?.id) return;
    const draft = this.opsNotesDrafts.get(job.id);
    if (draft === undefined) return;
    if (draft === (saved || '')) {
      this.opsNotesDrafts.delete(job.id);
      return;
    }
    this.opsNotesSaving = true;
    // Release only a slot this call actually holds: if acquire() is
    // interrupted (unsubscribed while waiting) the finally must not
    // decrement a counter owned by another ops write (CodeRabbit).
    let opsSlotHeld = false;
    try {
      // Serialize against the normal save-changes flow and 📎 uploads: a
      // concurrent normal save builds its metadata snapshot before this
      // write lands and would restore the old summary when its (later)
      // response arrives (CodeRabbit: "Coordinate Ops Summary saves").
      // Atomically waits until no other writer is active and claims the
      // ops slot in the same emission.
      await firstValueFrom(this.saveGate.acquire('ops'));
      opsSlotHeld = true;
      const res = await firstValueFrom(this.updateJobGQL.mutate({
        updateJobs: {
          jobId: job.id,
          metadata: { calculatorOutput__opsNotes: draft },
        },
      }));
      // Sync the NgRx job state with the saved metadata BEFORE dropping the
      // draft: selectOpsNotes still reads the job's previous value, so the
      // textarea would revert to stale text and a later edit could overwrite
      // the saved summary (CodeRabbit).
      const updateRes = res?.data?.updateJobs;
      const updatedJob = updateRes?.jobs?.[0];
      if (!updateRes?.isSuccess || !updatedJob) {
        // The server did not confirm the write — keep the draft so the
        // user's text survives, and tell them (CodeRabbit).
        throw new Error('Ops summary save not confirmed');
      }
      this.store.dispatch(JobToolActions.updateJobMetadataSuccess({
        updatedJob,
      }));
      this.opsNotesDrafts.delete(job.id);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Save failed',
        detail: 'Could not save the Ops Summary. Please try again.',
      });
    } finally {
      if (opsSlotHeld) { this.saveGate.endOpsWrite(); }
      this.opsNotesSaving = false;
    }
  }

}
